import { useMemo, useState } from 'react';
import { Calculator, Copy, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Fixed global: 1 credit = $0.0001 upstream cost. Shown next to USD fields so
// admins can eyeball "how much credit does this model burn per 1M tokens" —
// but the DB only stores the USD values; credits are always derived.
const CREDITS_PER_USD = 10_000;

export interface PricingSuggestion {
  cost_usd_per_1m_input_uncached: number;
  cost_usd_per_1m_input_cached: number;
  cost_usd_per_1m_output: number;
}

interface Props {
  onFillNew: (values: PricingSuggestion) => void;
}

export function PricingCalculator({ onFillNew }: Props) {
  const [open, setOpen] = useState(false);
  const [usdInUnc, setUsdInUnc] = useState('');
  const [usdInC, setUsdInC] = useState('');
  const [usdOut, setUsdOut] = useState('');
  const [copied, setCopied] = useState(false);

  const result = useMemo<PricingSuggestion | null>(() => {
    const unc = Number(usdInUnc);
    const c = Number(usdInC);
    const out = Number(usdOut);
    if (!Number.isFinite(unc) || !Number.isFinite(c) || !Number.isFinite(out)) return null;
    if (unc <= 0 || out <= 0 || c < 0) return null;
    if (c > unc) return null;
    return {
      cost_usd_per_1m_input_uncached: unc,
      cost_usd_per_1m_input_cached: c,
      cost_usd_per_1m_output: out,
    };
  }, [usdInUnc, usdInC, usdOut]);

  const anyInput = Boolean(usdInUnc || usdInC || usdOut);

  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!open) {
    return (
      <Button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="打开定价计算器"
        className="fixed right-4 bottom-6 z-40 h-11 w-11 rounded-full p-0 shadow-lg transition-transform hover:scale-105"
      >
        <Calculator className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <div className="fixed right-4 bottom-6 z-40 w-80 max-w-[calc(100vw-2rem)]">
      <Card className="shadow-xl">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            定价计算器
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              aria-label="关闭"
              className="ml-auto h-7 w-7 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground leading-snug">
            输入上游官方 USD 定价（per 1M tokens），一键填入新增表单。
            <br />
            扣费按固定规则 <b>1 credit = $0.0001</b> 自动换算（$1 = {CREDITS_PER_USD.toLocaleString()} credits）。
          </p>

          <div className="space-y-2">
            <Field label="USD input uncached / 1M">
              <Input
                type="number"
                step="0.000001"
                placeholder="如 0.897"
                value={usdInUnc}
                onChange={(e) => setUsdInUnc(e.target.value)}
              />
            </Field>
            <Field label="USD input cached / 1M">
              <Input
                type="number"
                step="0.000001"
                placeholder="如 0.152"
                value={usdInC}
                onChange={(e) => setUsdInC(e.target.value)}
              />
            </Field>
            <Field label="USD output / 1M">
              <Input
                type="number"
                step="0.000001"
                placeholder="如 3.724"
                value={usdOut}
                onChange={(e) => setUsdOut(e.target.value)}
              />
            </Field>
          </div>

          {result && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs font-mono space-y-1">
              <Row label="USD in uncached" value={`$${result.cost_usd_per_1m_input_uncached}`} />
              <Row label="USD in cached" value={`$${result.cost_usd_per_1m_input_cached}`} />
              <Row label="USD output" value={`$${result.cost_usd_per_1m_output}`} />
              <div className="border-t border-border/60 my-1" />
              <Row
                label="每 1M input 扣"
                value={`${Math.round(result.cost_usd_per_1m_input_uncached * CREDITS_PER_USD).toLocaleString()} credits`}
              />
              <Row
                label="每 1M output 扣"
                value={`${Math.round(result.cost_usd_per_1m_output * CREDITS_PER_USD).toLocaleString()} credits`}
              />
            </div>
          )}

          {!result && anyInput && (
            <p className="text-xs text-destructive">需要 3 个值都是正数，且 cached ≤ uncached</p>
          )}

          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={copy} disabled={!result} className="flex-1">
              <Copy className="h-3 w-3 mr-1" />
              {copied ? '已复制' : '复制 JSON'}
            </Button>
            <Button
              size="sm"
              onClick={() => result && onFillNew(result)}
              disabled={!result}
              className="flex-1"
            >
              填入新增表单
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
